import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styles from './GuildLeague.module.css';

// ── Types matching the backend camelCase responses ──────────────────────────

interface GuildInfo {
  id: number;
  name: string;
  tag: string;
  totalMembers?: number;
}

interface SeasonInfo {
  id: number;
  name: string;
  seasonNumber: number;
  romanNumeral: string;
}

interface LeagueInfo {
  id: number;
  name: string;
  status: 'upcoming' | 'active' | 'completed';
  startDate: string;
  endDate: string;
  currentRound: number;
  totalRounds: number;
  Season?: SeasonInfo;
}

interface LeagueStanding {
  id: number;
  guildId: number;
  points: number;
  wins: number;
  losses: number;
  kills: number;
  battlesPlayed: number;
  Guild: GuildInfo;
}

interface LeagueBattle {
  id: number;
  round: number;
  matchupId: number | null;
  gameNumber: number | null;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'failed';
  scheduledDate: string;
  winnerGuildId: number | null;
  maxLevel: number;
  battleReportId: number | null;
  Guild1: GuildInfo;
  Guild2: GuildInfo;
}

// ── Best-of-3 grouping types ─────────────────────────────────────────────────

interface MatchupGroup {
  matchupId: number | null;
  guild1: GuildInfo;
  guild2: GuildInfo;
  games: LeagueBattle[]; // sorted by gameNumber
}

interface RoundGroup {
  round: number;
  scheduledDate: string;
  matchups: MatchupGroup[];
}

// ── Helper: group flat battles into rounds → matchups → games ────────────────

const groupBattlesIntoRounds = (battleList: LeagueBattle[]): RoundGroup[] => {
  const roundMap = new Map<number, Map<string, MatchupGroup>>();

  for (const battle of battleList) {
    if (!roundMap.has(battle.round)) roundMap.set(battle.round, new Map());
    const matchupMap = roundMap.get(battle.round)!;

    // Use matchupId when available; fall back to sorted guild-pair key
    const key =
      battle.matchupId != null
        ? `mid-${battle.matchupId}`
        : `g-${Math.min(battle.Guild1.id, battle.Guild2.id)}-${Math.max(battle.Guild1.id, battle.Guild2.id)}`;

    if (!matchupMap.has(key)) {
      matchupMap.set(key, {
        matchupId: battle.matchupId,
        guild1: battle.Guild1,
        guild2: battle.Guild2,
        games: [],
      });
    }
    matchupMap.get(key)!.games.push(battle);
  }

  return Array.from(roundMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([round, matchupMap]) => ({
      round,
      scheduledDate: Array.from(matchupMap.values())[0]?.games[0]?.scheduledDate ?? '',
      matchups: Array.from(matchupMap.values()).map(m => ({
        ...m,
        games: [...m.games].sort((a, b) => (a.gameNumber ?? 0) - (b.gameNumber ?? 0)),
      })),
    }));
};

// ── Component ────────────────────────────────────────────────────────────────

const GuildLeague: React.FC = () => {
  const [leagueInfo, setLeagueInfo] = useState<LeagueInfo | null>(null);
  const [standings, setStandings] = useState<LeagueStanding[]>([]);
  const [battles, setBattles] = useState<LeagueBattle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'standings' | 'schedule' | 'results'>('standings');

  useEffect(() => {
    const fetchLeagueData = async () => {
      try {
        setLoading(true);

        const infoRes = await axios.get('/api/guild-league/current', { withCredentials: true });
        const league: LeagueInfo = infoRes.data;
        setLeagueInfo(league);

        const [standingsRes, battlesRes] = await Promise.all([
          axios.get(`/api/guild-league/${league.id}/standings`, { withCredentials: true }),
          axios.get(`/api/guild-league/${league.id}/battles`, { withCredentials: true }),
        ]);

        setStandings(standingsRes.data);
        setBattles(battlesRes.data);
      } catch (err: any) {
        console.error('Error fetching league data:', err);
        if (err.response?.status === 404) {
          setLeagueInfo(null);
        } else {
          setError(err.response?.data?.message || 'Failed to load league data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueData();
  }, []);

  if (loading) {
    return (
      <div className={styles.loading}>
        Loading League Data...
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  // Each row is a real game in a best-of-3 series, no deduplication needed.
  // Group: all rounds → matchups (by matchupId) → games (sorted by gameNumber).
  const allRoundGroups = groupBattlesIntoRounds(battles);

  // Schedule tab: rounds that have at least one pending/active game
  const scheduleRounds = allRoundGroups.filter(rg =>
    rg.matchups.some(m => m.games.some(g => g.status !== 'completed' && g.status !== 'cancelled' && g.status !== 'failed'))
  );

  // Results tab: rounds that have at least one completed game (most recent first)
  const resultsRounds = allRoundGroups
    .filter(rg => rg.matchups.some(m => m.games.some(g => g.status === 'completed')))
    .slice()
    .reverse();

  const fmtDate = (d: string) =>
    new Date(d).toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const getLeagueStatusClass = (status: string) => {
    switch (status) {
      case 'upcoming':  return styles.statusUpcoming;
      case 'active':    return styles.statusActive;
      case 'completed': return styles.statusCompleted;
      default:          return styles.statusCompleted;
    }
  };

  const getLeagueStatusLabel = (status: string) => {
    switch (status) {
      case 'upcoming':  return '⏳ Upcoming';
      case 'active':    return '⚔ Active';
      case 'completed': return '✓ Completed';
      default:          return status.toUpperCase();
    }
  };

  const roundHasActiveGame = (matchups: MatchupGroup[]) =>
    matchups.some(m => m.games.some(g => g.status === 'in_progress'));

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h1 className={styles.title}>Guild League</h1>
        <p className={styles.subtitle}>
          {leagueInfo
            ? (leagueInfo.Season
                ? `Season ${leagueInfo.Season.romanNumeral}`
                : leagueInfo.name)
            : 'Offseason'}
        </p>
        <div className={styles.headerDivider} />
      </div>

      {error ? (
        <div className={styles.errorCard}>{error}</div>
      ) : !leagueInfo ? (
        <div className={styles.noLeagueCard}>
          <h2>Offseason</h2>
          <p>There is no active Guild League at the moment. Check back when the new season begins!</p>
        </div>
      ) : (
        <>
          <div className={styles.leagueInfo}>
            <span className={`${styles.statusBadge} ${getLeagueStatusClass(leagueInfo.status)}`}>
              {getLeagueStatusLabel(leagueInfo.status)}
            </span>
            <div className={styles.infoItem}>
              Round: <strong>{leagueInfo.currentRound} / {leagueInfo.totalRounds}</strong>
            </div>
            <div className={styles.infoItem}>
              Started: <strong>{new Date(leagueInfo.startDate).toLocaleDateString('en-GB')}</strong>
            </div>
          </div>

          <div className={styles.tabs}>
            <button
              className={`${styles.tabButton} ${activeTab === 'standings' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('standings')}
            >
              Standings
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'schedule' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              Upcoming Matches
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'results' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('results')}
            >
              Results
            </button>
          </div>

          <div className={styles.tabContent}>

            {/* ── Standings ── */}
            {activeTab === 'standings' && (
              <div className={styles.standingsContainer}>
                <table className={styles.standingsTable}>
                  <thead>
                    <tr>
                      <th className={styles.position}>#</th>
                      <th>Guild</th>
                      <th>Played</th>
                      <th>W</th>
                      <th>L</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.length === 0 ? (
                      <tr>
                        <td colSpan={6} className={styles.noData}>No standings available yet.</td>
                      </tr>
                    ) : (
                      standings.map((standing, index) => {
                        const rankClass =
                          index === 0 ? styles.rank1 :
                          index === 1 ? styles.rank2 :
                          index === 2 ? styles.rank3 : '';
                        return (
                          <tr key={standing.id} className={rankClass}>
                            <td className={styles.position}>{index + 1}</td>
                            <td>
                              <Link to={`/guild/${standing.Guild.id}`} className={styles.guildLink}>
                                <div className={styles.guildName}>
                                  {standing.Guild.name}
                                  <span className={styles.tag}>[{standing.Guild.tag}]</span>
                                </div>
                              </Link>
                            </td>
                            <td>{standing.battlesPlayed}</td>
                            <td className={styles.wins}>{standing.wins}</td>
                            <td className={styles.losses}>{standing.losses}</td>
                            <td className={styles.points}>{standing.points}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Upcoming Matches ── */}
            {activeTab === 'schedule' && (
              <>
                {scheduleRounds.length === 0 ? (
                  <div className={styles.noBattles}>
                    <p>No upcoming matches scheduled.</p>
                    {leagueInfo.status === 'upcoming' && (
                      <p className={styles.hint}>Matches will be generated when the season starts.</p>
                    )}
                  </div>
                ) : (
                  scheduleRounds.map(rg => {
                    const isActive = roundHasActiveGame(rg.matchups);
                    return (
                      <div key={rg.round} className={styles.roundGroup}>
                        <div className={styles.roundHeader}>
                          <span className={styles.roundLabel}>Round {rg.round}</span>
                          <span className={styles.roundDate}>{fmtDate(rg.scheduledDate)}</span>
                          <span className={`${styles.battleStatus} ${isActive ? styles.statusInProgress : styles.statusScheduled}`}>
                            {isActive ? 'In Progress' : 'Scheduled'}
                          </span>
                        </div>
                        <div className={styles.roundMatchups}>
                          {rg.matchups.map((matchup, mi) => (
                            <div key={mi} className={styles.matchupRow}>
                              <div className={styles.matchupTeams}>
                                <span className={styles.matchupGuild}>
                                  {matchup.guild1.name}
                                  <span className={styles.tag}> [{matchup.guild1.tag}]</span>
                                </span>
                                <span className={styles.matchupVs}>vs</span>
                                <span className={styles.matchupGuild}>
                                  {matchup.guild2.name}
                                  <span className={styles.tag}> [{matchup.guild2.tag}]</span>
                                </span>
                              </div>
                              <div className={styles.matchupGames}>
                                {matchup.games.map(game => (
                                  <span
                                    key={game.id}
                                    className={`${styles.gameSlot} ${
                                      game.status === 'in_progress' ? styles.gameActive :
                                      game.status === 'completed'   ? styles.gameDone   :
                                      styles.gamePending
                                    }`}
                                    title={`Game ${game.gameNumber ?? '?'}: ${game.status}`}
                                  >
                                    G{game.gameNumber ?? '?'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}

            {/* ── Results ── */}
            {activeTab === 'results' && (
              <>
                {resultsRounds.length === 0 ? (
                  <div className={styles.noBattles}>
                    <p>No completed matches yet.</p>
                  </div>
                ) : (
                  resultsRounds.map(rg => (
                    <div key={rg.round} className={styles.roundGroup}>
                      <div className={styles.roundHeader}>
                        <span className={styles.roundLabel}>Round {rg.round}</span>
                        <span className={styles.roundDate}>
                          {new Date(rg.scheduledDate).toLocaleDateString('en-GB', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </span>
                      </div>
                      <div className={styles.roundMatchups}>
                        {rg.matchups.map((matchup, mi) => {
                          const completedGames = matchup.games.filter(g => g.status === 'completed');
                          const g1wins = completedGames.filter(g => g.winnerGuildId === matchup.guild1.id).length;
                          const g2wins = completedGames.filter(g => g.winnerGuildId === matchup.guild2.id).length;
                          const seriesWinnerId = g1wins >= 2 ? matchup.guild1.id : g2wins >= 2 ? matchup.guild2.id : null;

                          return (
                            <div key={mi} className={styles.matchupRow}>
                              <div className={styles.matchupTeams}>
                                <span className={`${styles.matchupGuild} ${seriesWinnerId === matchup.guild1.id ? styles.matchupWinner : ''}`}>
                                  {seriesWinnerId === matchup.guild1.id && <span className={styles.crown}>👑 </span>}
                                  {matchup.guild1.name}
                                  <span className={styles.tag}> [{matchup.guild1.tag}]</span>
                                </span>
                                {completedGames.length > 0 && (
                                  <span className={styles.matchupScore}>{g1wins}–{g2wins}</span>
                                )}
                                <span className={`${styles.matchupGuild} ${seriesWinnerId === matchup.guild2.id ? styles.matchupWinner : ''}`}>
                                  {seriesWinnerId === matchup.guild2.id && <span className={styles.crown}>👑 </span>}
                                  {matchup.guild2.name}
                                  <span className={styles.tag}> [{matchup.guild2.tag}]</span>
                                </span>
                              </div>
                              <div className={styles.matchupGames}>
                                {matchup.games.map(game =>
                                  game.status === 'completed' ? (
                                    game.battleReportId ? (
                                      <a
                                        key={game.id}
                                        href={`/battle-result/${game.battleReportId}`}
                                        className={styles.brButton}
                                        title={`Game ${game.gameNumber} battle report`}
                                      >
                                        G{game.gameNumber ?? '?'}
                                      </a>
                                    ) : (
                                      <span key={game.id} className={`${styles.gameSlot} ${styles.gameDone}`}>
                                        G{game.gameNumber ?? '?'}
                                      </span>
                                    )
                                  ) : game.status === 'in_progress' ? (
                                    <span key={game.id} className={`${styles.gameSlot} ${styles.gameActive}`}>
                                      G{game.gameNumber ?? '?'}
                                    </span>
                                  ) : null
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

          </div>
        </>
      )}
    </div>
  );
};

export default GuildLeague;
